import type { ThemeKey, DefaultThemeStyles } from 'Src/types/theme';
import type { ReactNode } from 'react';
import React, { useId, useState, useEffect, useCallback } from 'react';
import { isEmpty, noop } from 'lodash';
import { cleanupId } from 'Utils/unique-id/uniqueId';
import { getFormattedLabel } from 'Components/utils/form/form.util';
import { ICONS } from 'Components/utils/icon/icons';
import type { IconName } from 'Components/frameworks/icon/Icon';
import { Icon } from 'Components/frameworks/icon/Icon';
import { FormControl } from 'Components/frameworks/form-inputs/form-control/FormControl';
import { themes } from './InputSelect.themes';
type Props = {
  caretIcon?: IconName;
  className?: string;
  dataTestId?: string;
  hasClear?: boolean;
  iconLeft?: IconName;
  id?: string;
  inputId?: string;
  isDisabled?: boolean;
  isFilled?: boolean;
  isLoading?: boolean;
  isReadOnly?: boolean;
  isRequired?: boolean;
  label?: string | ReactNode;
  name?: string;
  notes?: string;
  onBlur?: (arg0: React.SyntheticEvent) => void;
  onChange?: (name: string, value: string) => void;
  onClear?: (arg0: React.SyntheticEvent) => void;
  onClick?: (arg0: React.SyntheticEvent) => void;
  onFocus?: (arg0: React.SyntheticEvent) => void;
  options?: SelectProps;
  placeholder?: string;
  theme?: ThemeKey<typeof themes>;
  validationState?: string;
  value?: string;
};
const defaultProps = {
  caretIcon: ICONS.ARROW_DOWN,
  options: [],
  theme: 'default' as ThemeKey<typeof themes>,
};
export function InputSelect(rawProps: Props) {
  const props = { ...defaultProps, ...rawProps };
  const {
    caretIcon,
    className,
    hasClear,
    iconLeft,
    isDisabled,
    isFilled,
    isLoading,
    isReadOnly,
    isRequired,
    label,
    name,
    notes,
    onBlur,
    onChange = noop,
    onClear: onClearProp = noop,
    onClick,
    onFocus,
    options,
    placeholder,
    theme,
    validationState,
    dataTestId,
    inputId,
  } = props;

  const reactId = useId();
  const labelStr = typeof label === 'string' ? cleanupId(label) : '';
  const id = props.id ?? (labelStr ? `InputSelect_${labelStr}` : `InputSelect_${reactId}`);

  const [value, setValue] = useState(props.value || '');

  useEffect(() => {
    if (props.value !== undefined) {
      setValue(props.value);
    }
  }, [props.value]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      if (!isReadOnly && !isLoading) {
        const { value: newValue } = e.target;
        setValue(newValue);
        onChange(name ?? '', newValue);
      }
    },
    [isReadOnly, isLoading, name, onChange],
  );

  const handleClear = useCallback(
    (e: React.SyntheticEvent) => {
      setValue('');
      onClearProp(e);
    },
    [onClearProp],
  );

  function getOptions(): Array<ReactNode | null | undefined> | null | undefined {
    if (isEmpty(options)) {
      return null;
    }

    return options.map(
      ({ isDisabled: optionDisabled, isSelected, label: optionLabel, value: optionValue }) => {
        return optionLabel ? (
          <option
            disabled={optionDisabled}
            key={optionLabel}
            label={optionLabel}
            selected={isSelected}
            value={optionValue}
          >
            {optionLabel}
          </option>
        ) : null;
      },
    );
  }

  const hasClearEnabled = Boolean(hasClear && value);
  const hasValue = Boolean(value);
  const placeholderFormatted = getFormattedLabel(placeholder, isRequired);
  const { thCaret, thInput, thInputWrapper, thPlaceholder } = themes[theme](
    props,
  ) as DefaultThemeStyles<typeof themes>;
  return (
    <FormControl
      className={className}
      hasClear={hasClearEnabled}
      id={id}
      iconLeft={iconLeft}
      isDisabled={isDisabled}
      isFilled={isFilled && hasValue}
      isLoading={isLoading}
      isReadOnly={isReadOnly}
      isRequired={isRequired}
      label={label}
      notes={notes}
      onClear={handleClear}
      theme={theme}
      validationState={validationState}
    >
      <div className={thInputWrapper}>
        <select
          className={thInput}
          disabled={Boolean(isDisabled || isLoading || isReadOnly)}
          id={inputId ? inputId : id}
          {...(name
            ? {
                name,
              }
            : null)}
          onBlur={onBlur}
          onChange={handleChange}
          onClick={onClick}
          onFocus={onFocus}
          required={isRequired}
          value={value}
          data-testid={dataTestId}
        >
          {!!placeholderFormatted && (
            <option className={thPlaceholder} label={placeholderFormatted} disabled value="">
              {placeholderFormatted}
            </option>
          )}
          {getOptions()}
        </select>
        {!(isDisabled || isLoading || isReadOnly) && (
          <div className={thCaret} data-testid="InputSelectCaret">
            <Icon
              name={caretIcon}
              width="0.75rem"
              dataTestid={caretIcon ? `caret-${caretIcon}` : ''}
            />
          </div>
        )}
      </div>
    </FormControl>
  );
}
